import {useForm} from '@tanstack/react-form';
import React from 'react';
import {Button} from '~/components/Button';
import {EditorInput} from '~/components/Controls/Editor';
import {FormColumn, FormControls, FormRow, FormTitle, IntentText} from '~/components/Form';
import formStyle from '~/components/Form/index.module.scss';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {Intent} from '~/lib/intent';
import {formatJsonValue} from '~/lib/json';
import {IFeature} from '~/modules/Features/models/Feature';

export interface IFeatureFormActions {
    setStatus: (status: string | undefined) => void;
}

export interface IFeatureFormValues {
    name: string;
    value: unknown;
}

export interface IFeatureFormProps {
    feature: IFeature;
    onSubmit?: (values: IFeatureFormValues, formActions: IFeatureFormActions, close: IModalComponentClose) => void | Promise<void>;
}

export const FeatureForm: IModalComponent<IFeatureFormProps> = props => {
    const closeModal = props.close ?? (() => undefined);
    const [status, setStatus] = React.useState<string>();
    const {feature} = props;

    const form = useForm({
        defaultValues: {
            name: feature.name,
            value: formatJsonValue(feature.value)
        },
        onSubmit: async ({value: formValues}) => {
            if (!props.onSubmit) return;
            let parsedValue: unknown;
            try {
                parsedValue = JSON.parse(formValues.value);
            } catch {
                setStatus('Value must be valid JSON');
                return;
            }
            await Promise.resolve(props.onSubmit({name: formValues.name, value: parsedValue}, {setStatus}, closeModal));
        }
    });

    return <form.Subscribe selector={state => state}>
        {state => (
            <form className={formStyle.form} onSubmit={event => {
                event.preventDefault();
                void form.handleSubmit();
            }}>
                <FormTitle>Edit {feature.name}</FormTitle>
                {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
                <div className={formStyle.formBody}>
                    <FormRow caption='Value'>
                        <FormColumn>
                            <form.Field name='value'>
                                {fieldApi => (
                                    <EditorInput
                                        mode='json'
                                        canCopy
                                        minLines={4}
                                        maxLines={20}
                                        value={fieldApi.state.value}
                                        onChange={value => fieldApi.handleChange(value)}
                                        disabled={state.isSubmitting}
                                    />
                                )}
                            </form.Field>
                        </FormColumn>
                    </FormRow>
                </div>
                <FormControls>
                    <Button disabled={state.isSubmitting} onClick={closeModal}>Cancel</Button>
                    <Button type='submit'
                        disabled={state.isSubmitting}
                        intent={Intent.PRIMARY}
                        rightIcon={state.isSubmitting && <i className='fa fa-spinner fa-spin'/>}
                    >Save</Button>
                </FormControls>
            </form>
        )}
    </form.Subscribe>;
};
